<template>
  <div class="neurofeedback-sidebar">
    <div class="title">{{ isManual ? "Feedback marker" : "Emotion peak" }}</div>
    <bg-loader
      :dark="true"
      style="position: relative; float: right; top: -23px; left: 0"
      v-if="savingInProgress"
      type="circle"
    ></bg-loader>
    <transition name="fade">
      <div class="checkmark" v-if="savingSuccessfull"></div>
    </transition>
    <div :class="`emotion emotion--${emotion}`">
      {{ isManual ? "Manual feedback" : emotion }}
    </div>
    <div class="small-title">Participant feedback:</div>
    <div class="feedback">
      <editable-content
        v-model="content"
        @focusLost="saveChangesToSignificantPoint"
        :uniqueKey="this.significantPointID"
      ></editable-content>
    </div>
    <!-- <bg-checkbox
      key="very_unique_key"
      v-model="falsePositive"
      label="Mark peak as false positive"
      class="false-positive-checkbox"
      :outline="true"
      :disabled="false"
      @change="saveChangesToSignificantPoint"
    /> -->
    <advanced-select
      prompt="Select tags"
      add-prompt="Add tag"
      form-prompt="Tag name"
      :options="tagsOptions"
      :selected="selectedTags"
      entity="tag"
      @create="handleCreateTag"
      @update="handleUpdateTag"
      @delete="handleDeleteTag"
      @change="handleRelationChange"
      style="margin-top: 10px;"
    >
    </advanced-select>
    <div v-if="testPlatform !== 'video'">
      <p style="margin-bottom: 5px; margin-top: 15px">
        <b>Assign to an Insight:</b>
      </p>

      <bg-select
        id="insightSelect"
        ref="insightSelect"
        :options="insightsOptions"
        :label="label"
        @change="onSelect"
      ></bg-select>
    </div>
    <div
      v-if="deletable"
      class="delete-point"
      title="Delete this point from the timeline"
      @click="$emit('requestDelete')"
    >
      <bg-icon name="fdelete"></bg-icon>
      <span>{{ isManual ? "Delete feedback marker" : "Delete emotion peak" }}</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.neurofeedback-sidebar {
  height: 100%;
  background-color: rgba(177, 177, 193, 0.2);
  margin-left: 25px;
  padding: 30px 20px 20px 20px;
  width: 290px;
}
.feedback {
  width: 100%;
  min-height: 200px;
  border-radius: 10px;
  background-color: #fff;
  padding: 15px;
}
.title {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 10px;
}
.emotion {
  font-weight: bold;

  font-size: 16px;
  text-transform: capitalize;
  &--frustration {
    color: #ff00a9;
  }
  &--joy {
    color: #17dfb1;
  }
  &--attention {
    color: #f1c40f;
  }
  &--manual {
    color: var(--bg-color-gray-dark);
  }
}
.delete-point {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 25px;
  font-size: 14px;
  cursor: pointer;
  color: var(--bg-color-gray-dark);
  &:hover {
    color: #ff00a9;
  }
  ::v-deep svg {
    width: 16px;
    height: 16px;
  }
}
.small-title {
  margin: 10px 0px;
  font-size: 16px;
  font-weight: bold;
}
.checkmark {
  transform: rotate(45deg);
  height: 20px;
  width: 12px;
  border-bottom: 3px solid #00deab;
  border-right: 3px solid #00deab;
  float: right;
  position: relative;
  top: -37px;
  right: 10px;
}
.false-positive-checkbox {
  margin-top: 15px;
}
</style>

<script>
import API from "@/api";
import EditableContent from "@/components/editable-content";
import AdvancedSelect from "@/components/advanced-select";
import BgLoader from "@/components/bg-loader";
import BgSelect from "@/components/bg-select";
import BgIcon from "@/components/bg-icon";
//

export default {
  name: "NeurofeedbackInteractiveSidebar",
  components: {
    AdvancedSelect,
    EditableContent,
    BgLoader,
    BgSelect,
    BgIcon,
  },
  computed: {
    isManual() {
      return this.emotion === "manual";
    },
  },
  data() {
    return {
      savingInProgress: false,
      savingSuccessfull: false,
      content: this.significantPointComment,
      emotion: this.significantPointEmotion,
      falsePositive: this.significantPointFalsePositive,
      multiple: "multiple",
      selectedTags: this.significantPointTags.map((tag) => tag.id),
      tagsOptions: this.tags,
      insights: [],
      label: "title",
    };
  },
  props: {
    insightsOptions: Array,
    significantPointComment: String,
    significantPointID: Number,
    significantPointEmotion: String,
    significantPointFalsePositive: Boolean,
    significantPointTags: Array,
    tags: Array,
    testId: Number,
    testPlatform: {
      type: String,
      optional: true,
    },
    deletable: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    significantPointID() {
      this.content = this.significantPointComment;
      this.emotion = this.significantPointEmotion;
      this.falsePositive = this.significantPointFalsePositive;
      this.selectedTags = this.significantPointTags.map((tag) => tag.id);
      // The insight select is not rendered on video tests.
      const child = this.$refs.insightSelect;
      if (child) child.resetInsightSelect();
    },
    significantPointComment() {
      this.content = this.significantPointComment;
    },
    significantPointEmotion() {
      this.emotion = this.significantPointEmotion;
    },
    significantPointFalsePositive() {
      this.falsePositive = this.significantPointFalsePositive;
    },
    significantPointTags() {
      this.selectedTags = this.significantPointTags.map((tag) => tag.id);
    },
  },
  methods: {
    saveChangesToSignificantPoint() {
      this.savingInProgress = true;
      this.$emit("selectedPointUpdated", {
        comment: this.content,
        false_positive: this.falsePositive,
      });
      API.participantData
        .updateSignificantPoint(this.significantPointID, {
          comment: this.content,
          false_positive: this.falsePositive,
        })
        .then(() => {
          this.savingInProgress = false;
          this.savingSuccessfull = true;
          setTimeout(() => {
            this.savingSuccessfull = false;
          }, 500);
        });
    },
    handleCreateTag(entityName, tag) {
      if (entityName === "tag") {
        this.savingInProgress = true;
        API.test
          .createTag(this.testId, { name: tag.name })
          .then(() => {
            return API.test.getTags(this.testId);
          })
          .then((res) => {
            this.tagsOptions = res.tags.map((tag) => {
              return { name: tag.name, value: tag.id };
            });
            this.savingInProgress = false;
            this.savingSuccessfull = true;
            setTimeout(() => {
              this.savingSuccessfull = false;
            }, 500);
          });
      }
    },
    handleUpdateTag(entityName, tag) {
      if (entityName === "tag") {
        this.savingInProgress = true;
        API.test
          .updateTag(tag.value, { name: tag.name })
          .then(() => {
            return API.test.getTags(this.testId);
          })
          .then((res) => {
            this.tagsOptions = res.tags.map((tag) => {
              return { name: tag.name, value: tag.id };
            });
            this.savingInProgress = false;
            this.savingSuccessfull = true;
            setTimeout(() => {
              this.savingSuccessfull = false;
            }, 500);
          });
      }
    },
    handleDeleteTag(entityName, tag) {
      if (entityName === "tag") {
        this.savingInProgress = true;
        API.test
          .deleteTag(tag.value)
          .then(() => {
            return API.test.getTags(this.testId);
          })
          .then((res) => {
            this.tagsOptions = res.tags.map((tag) => {
              return { name: tag.name, value: tag.id };
            });
            this.savingInProgress = false;
            this.savingSuccessfull = true;
            setTimeout(() => {
              this.savingSuccessfull = false;
            }, 500);
          });
      }
    },
    handleRelationChange(entityName, tagIds) {
      if (entityName === "tag") {
        this.savingInProgress = true;
        API.participantData
          .updateSignificantPoint(this.significantPointID, { tag_ids: tagIds })
          .then(() => {
            return API.participantData.getSignificantPoint(
              this.significantPointID
            );
          })
          .then((res) => {
            this.$emit("selectedPointUpdated", { tags: res.tags });
            this.savingInProgress = false;
            this.savingSuccessfull = true;
            setTimeout(() => {
              this.savingSuccessfull = false;
            }, 500);
          });
      }
    },
    onSelect(val) {
      this.$emit("change", val);
    },
  },
};
</script>
